import { formatRange } from '../../utils/parsing';

import {
    UserProfileType, WatchlistType, WatchlistForListType, WatchlistMatchMode
} from '../../types';

type WatchlistLike = Pick<WatchlistForListType, 'routeId' | 'routeName' | 'area' | 'source' | 'groupId'>;

export const MATCH_MODE_LABELS: Record<WatchlistMatchMode, string> = {
    within: 'Hela uppdraget sker inom området',
    touches: 'Uppdraget hämtas eller lämnas i området'
};

export const NOTIFICATION_HELP_TEXTS = {
    instantNotifications:
        'Du får en notis via push eller e-post direkt när ett nytt uppdrag eller en ny kapacitet matchar bevakningen.',
    dailyNotifications:
        'Du får en daglig summering av nya uppdrag och kapaciteter som matchar bevakningen via push eller e-post.',
    isJob: 'Bevakningen gäller uppdrag som publiceras av andra.',
    isCapacity: 'Bevakningen gäller kapacitet som publiceras av andra.',
    externalAssignments: 'Uppdrag från externa källor. Här kan du inte lämna intresseanmälan.'
};

export type WatchlistNotificationMode = 'off' | 'instant' | 'daily';

export const NOTIFICATION_MODE_LABELS: Record<WatchlistNotificationMode, string> = {
    off: 'Av',
    instant: 'Direkt',
    daily: 'Dagligen'
};

export const NOTIFICATION_MODE_HELP_TEXTS: Record<WatchlistNotificationMode, string> = {
    off: 'Bevakningen är pausad. Du får inga notiser från den.',
    instant: NOTIFICATION_HELP_TEXTS.instantNotifications,
    daily: 'En sammanställning per dag. Gäller idag DHL-uppdrag.'
};

/**
 * What the segmented control shows. Paused is always "off". If both channels are on (group watchlists
 * start that way) "instant" wins. Active with both channels off matches no option, so null: showing
 * "Av" would claim the watchlist is paused.
 */
export function getNotificationMode(
    w: Pick<WatchlistForListType, 'isActive' | 'instantNotifications' | 'dailyNotifications'>
): WatchlistNotificationMode | null {
    if (!w.isActive) {
        return 'off';
    }
    if (w.instantNotifications) {
        return 'instant';
    }
    return w.dailyNotifications ? 'daily' : null;
}

export const GROUP_AREA_LABEL = 'Gruppens uppdrag';

export type WatchlistTag = 'Rutt' | 'Grupp' | 'Eget område' | 'Hela landet' | typeof GROUP_AREA_LABEL | 'Pausad';

/** A group's watchlist without route or area follows the group's assignments, not the whole country. */
function followsOnlyGroup(w: Pick<WatchlistLike, 'routeId' | 'area' | 'groupId'>) {
    return !!w.groupId && !w.routeId && !w.area;
}

export function hasRoute(w: WatchlistLike) {
    return w.source === 'route' || w.source === 'residence' || !!w.routeId;
}

export function formatArea(w: WatchlistLike) {
    if (w.routeId) {
        return w.routeName ? `Rutt ${w.routeName}` : 'Rutt';
    }
    if (w.area?.type === 'circle') {
        return `Cirkel, radie ${Math.round(w.area.shape.radius / 1000)} km`;
    }
    if (w.area?.type === 'polygon') {
        return 'Eget område (polygon)';
    }
    return followsOnlyGroup(w) ? GROUP_AREA_LABEL : 'Hela landet';
}

/** Matchning only applies to a route or an area. */
export function formatMatchMode(w: Pick<WatchlistType, 'routeId' | 'area' | 'groupId' | 'matchMode'>) {
    if (w.routeId || w.area) {
        return MATCH_MODE_LABELS[w.matchMode];
    }
    return followsOnlyGroup(w) ? '–' : '';
}

export function getWatchlistTags(w: WatchlistLike & { isActive: boolean }): WatchlistTag[] {
    const tags: WatchlistTag[] = [];
    if (w.source === 'group') {
        tags.push('Grupp');
    }
    if (hasRoute(w)) {
        tags.push('Rutt');
    } else if (w.area) {
        tags.push('Eget område');
    } else {
        tags.push(followsOnlyGroup(w) ? GROUP_AREA_LABEL : 'Hela landet');
    }
    if (!w.isActive) {
        tags.push('Pausad');
    }
    return tags;
}

export function formatType({ isJob, isCapacity, takesOffer }: WatchlistForListType | WatchlistType) {
    const types = [];
    if (isJob) {
        types.push(takesOffer ? 'Uppdrag utan externa uppdrag' : 'Uppdrag');
    }
    if (isCapacity) {
        types.push('Kapacitet');
    }
    return types.join(' & ');
}

export function formatWatchlist(w: WatchlistForListType | WatchlistType) {
    return [
        formatType(w),
        formatArea(w),
        formatRange(w.minGrossWeight, w.maxGrossWeight, 'kg tot'),
        formatRange(w.minChargeableWeight, w.maxChargeableWeight, 'kg ldg'),
        formatRange(w.minVolume, w.maxVolume, 'm³'),
        formatRange(w.minLoadingMeters, w.maxLoadingMeters, 'flm'),
        formatRange(w.minPalletPlaces, w.maxPalletPlaces, 'ppl'),
        formatRange(w.minPallets, w.maxPallets, 'pallar'),
        // formatRange(w.minPieces, w.maxPieces, 'stycken'),
        w.groupName ? `I grupp ${w.groupName}` : '',
        w.hasListPrice ? 'Har riktpris' : ''
    ].filter((v) => !!v).join(', ');
}

/** Delete confirmation by source. Route and group watchlists don't come back, so Av is offered while active. */
export function getDeleteConfirmation({ source, isActive }: Pick<WatchlistForListType, 'source' | 'isActive'>) {
    const title = 'Ta bort bevakningen?';
    const place = { route: 'rutten', residence: 'rutten', group: 'gruppen' }[source as string];
    if (!place) {
        return {
            title,
            body: 'Bevakningen tas bort och du får inga fler notiser från den. Det går inte att ångra.',
            canPause: false
        };
    }
    return {
        title,
        body: `Du får inga fler notiser för den här ${place}, och bevakningen kommer inte tillbaka av sig själv.${
            isActive ? ' Vill du behålla den kan du välja Av i stället.' : ''}`,
        canPause: isActive
    };
}

/** Warning for the watchlist views when email cannot arrive; other channels decide if any notifications do. */
export function getNotificationWarning(profile: Pick<UserProfileType,
    'emailNotifications' | 'emailVerified' | 'pushNotifications'>) {
    if (profile.emailNotifications && profile.emailVerified) {
        return null;
    }
    const reason = profile.emailVerified ? 'e-postnotiser är avstängda' : 'din e-postadress inte är verifierad';
    // SMS disabled (LL-419): `|| (profile.smsNotifications && profile.phoneVerified)` after pushNotifications,
    // and 'smsNotifications' | 'phoneVerified' in the Pick above
    return profile.pushNotifications
        ? `Du får inga mejl från dina bevakningar eftersom ${reason}.`
        : `Du får inga notiser från dina bevakningar eftersom ${reason} och inga andra kanaler är på.`;
}

export function formatNotificationCount(count: number) {
    return `${count} ${count === 1 ? 'notis' : 'notiser'} senaste 30 dagarna`;
}

/**
 * Keeps the type checkboxes consistent: "Externa uppdrag" (shown ticked when takesOffer is false) narrows
 * Uppdrag only (capacity is unaffected), so it requires Uppdrag and takesOffer is cleared when Uppdrag is turned off.
 */
export function applyTypeRules(change: { isJob?: boolean, isCapacity?: boolean, takesOffer?: boolean }) {
    if (change.takesOffer === true) return { ...change, isJob: true };
    if (change.isJob === false) return { ...change, takesOffer: false };
    return change;
}
